import { Button, Chip, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { getProblematicOrders, regenerateOrder } from '../api/super-admin';
import { CopyButton } from './copy-button';
import { ErrorMessage } from './error-message';

type ProblematicOrder = {
  order_id: number;
  transaction_id: string;
  order_status: string;
  order_item_id: number;
  uid: number;
  model: string;
  provider: string;
  geography: string;
  version: string | null;
  lat: number;
  lon: number;
  path: string | null;
  filename: string | null;
  parquet_key: string | null;
  stored?: boolean | null;   // only present when ?verify=true
  parquet?: string;          // 'ok' | 'missing' | 'no-key' | 'error:...'
  json?: string;             // 'ok' | 'missing' | 'no-key' | 'error:...'
  check?: string;            // 'ok' | 'missing:parquet' | 'missing:json' | 'missing:both' | 'error'
};

type RowStatus = 'idle' | 'running' | 'done' | 'error';

export const SuperAdminSettingsOrders = () => {
  const [orders, setOrders] = useState<ProblematicOrder[]>([])
  const [apiError, setApiError] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  // Verify against storage by default so the grid shows genuinely-missing orders,
  // not just every processed candidate. Untick to list candidates without the
  // per-order S3 check (faster).
  const [verify, setVerify] = useState(false)
  const [rowStatus, setRowStatus] = useState<Record<number, RowStatus>>({})

  const fetchOrders = useCallback(async () => {
    setLoading(true)
    const res = await getProblematicOrders({ verify })
    const { error, response, message } = res

    if (error === 200 && response) {
      setOrders(response?.results || [])
      setApiError([])
    } else {
      setApiError([message || 'An error occurred while fetching problematic orders.'])
    }
    setLoading(false)
  }, [verify])

  useEffect(() => {
    fetchOrders()
  }, [])

  const handleRegenerate = useCallback(async (row: ProblematicOrder) => {
    setRowStatus((s) => ({ ...s, [row.order_item_id]: 'running' }))
  
    const res = await regenerateOrder({
      order_id: row.order_id,
      order_item_id: row.order_item_id,
      transaction_id: row.transaction_id,
      uid: row.uid,
    })
    const { error, message } = res
  
    if (error === 200) {
      // Regenerate is order-level, so it fixes every leg of this order — drop them all.
      setOrders((prev) => prev.filter((o) => o.order_id !== row.order_id))
      setRowStatus((s) => {
        const next = { ...s }
        delete next[row.order_item_id]
        return next
      })
    } else {
      setRowStatus((s) => ({ ...s, [row.order_item_id]: 'error' }))
      setApiError([message || `Failed to regenerate order ${row.transaction_id}.`])
    }
  }, []);

  const columns: any = [
    {
      field: 'transaction_id',
      headerName: 'Order',
      width: 180,
      editable: false,
      renderCell: (params: any) => (
        <span>
          {params?.row?.transaction_id}
          <CopyButton content={params?.row?.transaction_id || ''} />
        </span>
      ),
    },
    {
      field: 'order_item_id',
      headerName: 'Item',
      type: 'number',
      width: 90,
      editable: false,
    },
    {
      field: 'uid',
      headerName: 'UID',
      type: 'number',
      width: 80,
      editable: false,
    },
    {
      field: 'dataset',
      headerName: 'Dataset',
      width: 240,
      editable: false,
      sortable: false,
      // renderCell (not valueGetter) so it's independent of the MUI X valueGetter
      // signature and always has access to the full row.
      renderCell: (params: any) => {
        const r = params?.row || {}
        return [r.provider, r.model, r.geography, r.version].filter(Boolean).join(' / ')
      },
    },
    {
      field: 'lat',
      headerName: 'Lat',
      type: 'number',
      width: 100,
      editable: false,
    },
    {
      field: 'lon',
      headerName: 'Lon',
      type: 'number',
      width: 100,
      editable: false,
    },
    {
      field: 'filename',
      headerName: 'Filename',
      width: 200,
      editable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: any) => {
        const r = params?.row || {}
        if (r.stored === true)  return <Chip size="small" color="success" label="Stored" />
        if (r.stored === null)  return <Chip size="small" color="warning" label="Check failed" />
        if (r.stored === false) {
          const label =
            r.check === 'missing:both'    ? 'Missing both' :
            r.check === 'missing:parquet' ? 'Missing data' :
            r.check === 'missing:json'    ? 'Missing schema' : 'Failed'
          return <Chip size="small" color="error" label={label} />
        }
        return <Chip size="small" variant="outlined" label="Unknown" />   // stored === undefined: not verified
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      editable: false,
      sortable: false,
      renderCell: (params: any) => {
        const st: RowStatus = rowStatus[params?.row?.order_item_id] || 'idle'
        return (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={st === 'running'}
            onClick={() => handleRegenerate(params.row)}
          >
            {st === 'running'
              ? <CircularProgress size={16} color="inherit" />
              : st === 'done'
                ? 'Done'
                : st === 'error'
                  ? 'Retry'
                  : 'Regenerate'}
          </Button>
        )
      },
    },
  ];

  return (
    <div>
      <ErrorMessage message={apiError} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <FormControlLabel
          control={<Switch checked={verify} onChange={(e) => setVerify(e.target.checked)} />}
          label="Show only failed"
        />
        <Button variant="outlined" size="small" onClick={fetchOrders} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </div>

      <DataGrid
        rows={orders}
        columns={columns}
        getRowId={(row) => row.order_item_id}
        loading={loading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'transaction_id', sort: 'asc' }],
          },
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        pageSizeOptions={[25, 100, 200]}
        disableRowSelectionOnClick
      />
    </div>
  )
}